import React, { useCallback, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Alert, Skeleton, Slide, useMediaQuery, Box,  } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from 'react-query';
import useIsDesktop from '../../Hooks/useIsDesktop';
import API from '../../API';
import ChatScreen from '../ChatScreen';

// Smooth slide transition for modal
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ChatModalCreator = ({ open, onClose, creatorId }) => {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Optimized query with better caching and error handling
  const { data, isLoading, error } = useQuery({
    queryKey: ['conversationCreator', creatorId],
    queryFn: () => API.conversations.checkUserToCreator({ recipient_creator_id: creatorId }),
    enabled: !!open && !!creatorId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });

  // Prevent accidental closure on mobile
  const handleClose = useCallback(
    (event, reason) => {
      if (reason === 'backdropClick' && isMobile) return;
      onClose();
    },
    [onClose, isMobile]
  );

  // Polished, responsive modal styles (matches user-to-user chat modal)
  const modalStyles = useMemo(
    () => ({
      paper: {
        margin: isMobile ? 0 : theme.spacing(3),
        width: isMobile ? '100vw' : 'auto',
        maxWidth: isMobile ? '100vw' : isTablet ? '72vw' : '60vw',
        minWidth: isMobile ? '100vw' : 480,
        maxHeight: isMobile ? '100vh' : '90vh',
        height: isMobile ? '100vh' : 'auto',
        borderRadius: isMobile ? 0 : 4,
        overflow: 'hidden',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(22,22,24,0.85)'
            : 'rgba(255,255,255,0.9)',
        backdropFilter: 'saturate(140%) blur(10px)',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.35)}`,
        display: 'flex',
        flexDirection: 'column',
      },
      dialogTitle: {
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        minHeight: 64,
        position: 'sticky',
        top: 0,
        zIndex: 2,
        background:
          theme.palette.mode === 'dark'
            ? 'rgba(18,18,18,0.7)'
            : 'rgba(255,255,255,0.7)',
        backdropFilter: 'blur(8px)',
      },
      dialogContent: {
        p: 0,
        pb: 1,
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      },
      chatContainer: {
        flex: 1,
        minHeight: 0,
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        '& .MuiDialogContent-root, &': {
          scrollbarWidth: 'thin',
        },
      },
      handleBar: {
        width: 36,
        height: 4,
        borderRadius: 2,
        backgroundColor: theme.palette.divider,
        mx: 'auto',
        my: 1,
        display: isMobile ? 'block' : 'none',
      },
      closeButton: {
        color: theme.palette.text.secondary,
        '&:hover': { backgroundColor: theme.palette.action.hover },
      },
    }),
    [theme, isMobile, isTablet]
  );

  const TitleContent = (
    <>
      <Typography
        variant={isMobile ? 'h6' : 'h5'}
        component="h2"
        noWrap
        sx={{ fontWeight: 600, flex: 1, mr: 1 }}
        id="creator-chat-modal-title"
      >
        {data ? `Creator Chat #${data.conversation_id}` : 'Starting Creator Chat…'}
      </Typography>
      <IconButton onClick={onClose} aria-label="Close creator chat" sx={modalStyles.closeButton}>
        <CloseIcon />
      </IconButton>
    </>
  );

  // Loading state
  if (isLoading) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={!isDesktop}
        TransitionComponent={Transition}
        transitionDuration={280}
        keepMounted
        scroll="paper"
        PaperProps={{ sx: modalStyles.paper }}
        BackdropProps={{ sx: { backdropFilter: 'blur(2px)' } }}
        aria-labelledby="creator-chat-modal-title"
      >
        <DialogTitle sx={modalStyles.dialogTitle}>
          {isMobile && <Box sx={modalStyles.handleBar} />}
          <Skeleton variant="text" width={200} height={28} />
          <IconButton aria-label="Close creator chat" sx={modalStyles.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={modalStyles.dialogContent}>
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={420} animation="wave" />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={!isDesktop}
      TransitionComponent={Transition}
      transitionDuration={280}
      keepMounted
      scroll="paper"
      PaperProps={{ sx: modalStyles.paper }}
      BackdropProps={{ sx: { backdropFilter: 'blur(2px)' } }}
      aria-labelledby="creator-chat-modal-title"
      disableEscapeKeyDown={isMobile}
    >
      <DialogTitle sx={modalStyles.dialogTitle}>
        {isMobile && <Box sx={modalStyles.handleBar} />}
        {TitleContent}
      </DialogTitle>

      <DialogContent dividers sx={modalStyles.dialogContent}>
        {error && (
          <Alert severity="error" variant="outlined" sx={{ mx: 2, my: 1 }}>
            {error?.response?.data?.error || error.message || 'Failed to load creator conversation'}
          </Alert>
        )}

        <Box sx={modalStyles.chatContainer}>
          <ChatScreen
            showWarning={false}
            conversationId={data?.conversation_id}
            sx={{
              height: '100%',
              minHeight: isMobile ? '100%' : 520,
              maxHeight: '100%',
              flex: 1,
              pb: isMobile ? 'env(safe-area-inset-bottom)' : 0,
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChatModalCreator;
